<script>
  import { createEventDispatcher, onMount, onDestroy, tick } from "svelte";
  import { f7, Icon, Badge, Button } from "framework7-svelte";
  import { ENGINE_URL, BASE_URL } from "../services/service.config";
  import SoundAlertManager from "../utils/soundAlerts";
  import { TradingService } from "../services/tradingService";
  import { formatCapital, formatMoneyWithSign as formatMoney, isTierUnassigned, formatTierBadge } from "../utils/formatters";
  import UserSearchTypeahead from "./UserSearchTypeahead.svelte";
  import AuditClientTimeline from "./AuditClientTimeline.svelte";

  export let opened = false;
  export let clientTag = "";
  export let user = null;
  export let positions = [];
  export let orders = [];
  export let users = [];
  export let environment = "LIVE";
  export let pendingActionIds = new Set();

  const dispatch = createEventDispatcher();

  let activeTab = "sizing"; // "sizing" | "strategies" | "positions_orders" | "reassign_audit"
  let drawerEl;
  let previousActiveElement = null;

  // Authentic F1F2 Client Config State
  let isLoadingConfig = false;
  let isSavingConfig = false;
  let multiplier = 1.0;
  let tier = null; // "H" | "M" | "L" | "CUSTOM" | null
  let customLot = 25;
  let amount = 500000;
  let dwnAvgQty = 1;
  let clientState = "RUNNING"; // RUNNING, PAUSED, STOPPED
  let masterOptIn = false;

  // Strategies Matrix (All 7 authentic F1F2 strategies)
  let strategies = {
    S1: true,
    S2: true,
    S3: true,
    S4: true,
    DOWN_AVGING: true,
    REV: false,
    CNC: true,
  };
  let isSavingStrategies = false;

  // Live Stock Preview Scrips State
  let previewScrips = [];
  let isLoadingPreviewScrips = false;

  // Re-assign Tag State
  let reassignSelectedUser = null;
  let reassignSearchQuery = "";
  let isReassigning = false;

  // Reactive metrics for this client
  $: clientPositions = (positions || []).filter(
    (p) => (p.client || "").toUpperCase() === (clientTag || "").toUpperCase()
  );
  $: clientOrders = (orders || []).filter(
    (o) => (o.client || "").toUpperCase() === (clientTag || "").toUpperCase()
  );
  $: openPositions = clientPositions.filter(
    (p) => p.status === "OPEN" || (p.netQty || p.net_qty || p.filled_qty || 0) !== 0
  );
  $: activeOrders = clientOrders.filter((o) => {
    const s = (o.status || "").toUpperCase();
    return ["PENDING", "OPEN", "TRIGGER_PENDING", "ACK", "CANCELLING..."].includes(s);
  });
  $: totalClientPnl = clientPositions.reduce((acc, p) => acc + (p.pnl || 0), 0);

  // Watch for opening & UCC changes
  $: if (opened && clientTag) {
    onDrawerOpen();
  } else if (!opened) {
    onDrawerClose();
  }

  async function onDrawerOpen() {
    previousActiveElement = document.activeElement;
    document.body.style.overflow = "hidden";
    reassignSelectedUser = user || null;
    reassignSearchQuery = user ? `${user.fullName || ''} (${user.mobile || ''})`.trim() : "";
    await Promise.all([fetchClientEngineConfig(), loadPreviewScrips()]);
    await tick();
    if (drawerEl) {
      const firstBtn = drawerEl.querySelector("button, [tabindex='0']");
      if (firstBtn) firstBtn.focus();
    }
  }

  function onDrawerClose() {
    if (typeof document !== "undefined") {
      document.body.style.overflow = "";
      if (previousActiveElement && typeof previousActiveElement.focus === "function") {
        previousActiveElement.focus();
      }
    }
  }

  async function loadPreviewScrips() {
    if (previewScrips.length > 0) return;
    isLoadingPreviewScrips = true;
    try {
      const res = await TradingService.fetchCompanyArray({ limit: 6, trading_st: 1 });
      if (res && Array.isArray(res.data) && res.data.length > 0) {
        previewScrips = res.data;
      } else {
        throw new Error("No data returned");
      }
    } catch (e) {
      previewScrips = [
        { Stock_Name: "RELIANCE", Security_Code: 500325, H_qty: 5, M_qty: 2, L_qty: 1 },
        { Stock_Name: "SBIN", Security_Code: 500112, H_qty: 50, M_qty: 20, L_qty: 10 },
        { Stock_Name: "TCS", Security_Code: 532540, H_qty: 5, M_qty: 2, L_qty: 1 },
        { Stock_Name: "INFY", Security_Code: 500209, H_qty: 10, M_qty: 5, L_qty: 2 },
        { Stock_Name: "WIPRO", Security_Code: 507685, H_qty: 50, M_qty: 20, L_qty: 10 },
      ];
    } finally {
      isLoadingPreviewScrips = false;
    }
  }

  async function fetchClientEngineConfig() {
    if (!clientTag) return;
    const targetUcc = clientTag.trim().toUpperCase();
    isLoadingConfig = true;
    try {
      const data = await TradingService.fetchClientConfig(targetUcc, false);
      if (data.multiplier !== undefined && data.multiplier > 0) multiplier = Number(data.multiplier);
      if (data.tier !== undefined) {
        const rawTier = data.tier ? String(data.tier).toUpperCase().trim() : null;
        tier = rawTier === "NULL" || rawTier === "UNASSIGNED" ? null : rawTier;
      } else if (user && user.tier) {
        const rawTier = String(user.tier).toUpperCase().trim();
        tier = rawTier === "NULL" || rawTier === "UNASSIGNED" ? null : rawTier;
      }

      if (data.custom_lot !== undefined && data.custom_lot > 0) customLot = Number(data.custom_lot);
      else if (data.customLot !== undefined && data.customLot > 0) customLot = Number(data.customLot);
      else if (user && user.custom_lot) customLot = Number(user.custom_lot);

      if (data.amount !== undefined && data.amount > 0) amount = Number(data.amount);
      else if (data.investment_capital !== undefined && data.investment_capital > 0) amount = Number(data.investment_capital);
      else if (user && (user.amount || user.investment_capital)) amount = Number(user.amount || user.investment_capital);

      if (data.dwn_avg_qty !== undefined) dwnAvgQty = Number(data.dwn_avg_qty);
      else if (data.dwnAvgQty !== undefined) dwnAvgQty = Number(data.dwnAvgQty);
      else if (data.dwnAvg_qty !== undefined) dwnAvgQty = Number(data.dwnAvg_qty);
      else if (user && (user.dwn_avg_qty || user.dwnAvg_qty)) dwnAvgQty = Number(user.dwn_avg_qty || user.dwnAvg_qty);

      if (data.state) clientState = data.state;
      if (data.master_opt_in !== undefined) masterOptIn = Boolean(data.master_opt_in);
      else if (user && user.master_opt_in !== undefined) masterOptIn = Boolean(user.master_opt_in);

      if (data.strategies) {
        strategies = {
          S1: Boolean(data.strategies.S1),
          S2: Boolean(data.strategies.S2),
          S3: data.strategies.S3 !== undefined ? Boolean(data.strategies.S3) : true,
          S4: data.strategies.S4 !== undefined ? Boolean(data.strategies.S4) : true,
          DOWN_AVGING: data.strategies.DOWN_AVGING !== undefined ? Boolean(data.strategies.DOWN_AVGING) : true,
          REV: Boolean(data.strategies.REV),
          CNC: Boolean(data.strategies.CNC),
        };
      }
    } catch (e) {
      console.warn("Could not load client engine config:", e);
      if (user) {
        if (user.master_opt_in !== undefined) masterOptIn = Boolean(user.master_opt_in);
        if (user.tier) {
          const rawTier = String(user.tier).toUpperCase().trim();
          tier = rawTier === "NULL" || rawTier === "UNASSIGNED" ? null : rawTier;
        }
        if (user.multiplier) multiplier = Number(user.multiplier);
        if (user.custom_lot) customLot = Number(user.custom_lot);
        if (user.amount) amount = Number(user.amount);
      }
    } finally {
      isLoadingConfig = false;
    }
  }

  function handleKeyDown(e) {
    if (!opened) return;
    if (e.key === "Escape") {
      e.preventDefault();
      closeDrawer();
      return;
    }
    // Trap focus inside drawer
    if (e.key === "Tab" && drawerEl) {
      const focusableEls = drawerEl.querySelectorAll(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      );
      if (focusableEls.length === 0) return;
      const firstEl = focusableEls[0];
      const lastEl = focusableEls[focusableEls.length - 1];

      if (e.shiftKey) {
        if (document.activeElement === firstEl) {
          e.preventDefault();
          lastEl.focus();
        }
      } else {
        if (document.activeElement === lastEl) {
          e.preventDefault();
          firstEl.focus();
        }
      }
    }
  }

  function closeDrawer() {
    opened = false;
    dispatch("close");
  }

  // Multiplier fast preset update
  async function applyMultiplier(val) {
    multiplier = Math.round(Number(val) * 10) / 10;
    try {
      const endpoint = `${ENGINE_URL}/client/${encodeURIComponent(clientTag)}/multiplier`;
      const res = await fetch(endpoint, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ multiplier }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      f7.toast
        .create({
          text: `⚡ Multiplier updated to ${multiplier.toFixed(1)}x for ${clientTag}`,
          closeTimeout: 2000,
        })
        .open();
      dispatch("update", { clientTag, multiplier });
    } catch (err) {
      f7.toast.create({ text: `Failed to update multiplier: ${err.message}`, closeTimeout: 3000 }).open();
    }
  }

  // Full F1F2 Sizing Config Save
  async function saveSizingConfig() {
    if (tier === "CUSTOM" && (!customLot || Number(customLot) <= 0)) {
      f7.dialog.alert("Custom lot size must be at least 1 share.", "Validation Error");
      return;
    }
    if (multiplier < 0.1 || multiplier > 10.0) {
      f7.dialog.alert("Multiplier must be between 0.1x and 10.0x.", "Validation Error");
      return;
    }

    isSavingConfig = true;
    try {
      const payload = {
        tier: tier || null,
        custom_lot: tier === "CUSTOM" ? Number(customLot) : null,
        amount: Number(amount),
        multiplier: Number(multiplier),
        dwn_avg_qty: Number(dwnAvgQty),
      };

      await TradingService.saveClientConfig(clientTag, payload, false);

      f7.toast
        .create({
          text: `✓ F1F2 Tier & Sizing configuration saved for ${clientTag}!`,
          closeTimeout: 2500,
        })
        .open();
      dispatch("update", { clientTag, config: payload });
    } catch (err) {
      f7.dialog.alert(err.message, "Failed to Save Sizing Config");
    } finally {
      isSavingConfig = false;
    }
  }

  // Fast Strategy Matrix Toggle
  async function toggleStrategy(key) {
    strategies[key] = !strategies[key];
    strategies = { ...strategies };
    isSavingStrategies = true;
    try {
      const activeStrats = Object.entries(strategies || {})
        .filter(([_, active]) => Boolean(active))
        .map(([stratKey]) => stratKey);

      await TradingService.toggleAutoTrade(
        clientTag,
        masterOptIn,
        activeStrats,
        multiplier || 1.0
      );
      f7.toast
        .create({
          text: `Strategy matrix updated (${key}: ${strategies[key] ? "ENABLED" : "DISABLED"})`,
          closeTimeout: 1500,
        })
        .open();
      dispatch("update", { clientTag, strategies });
    } catch (err) {
      // revert on fail
      strategies[key] = !strategies[key];
      strategies = { ...strategies };
      f7.toast.create({ text: `Strategy update failed: ${err.message}`, closeTimeout: 3000 }).open();
    } finally {
      isSavingStrategies = false;
    }
  }

  // Master Auto-Trade Toggle
  async function toggleMasterOptIn() {
    const nextState = !masterOptIn;
    try {
      const activeStrats = Object.entries(strategies || {})
        .filter(([_, active]) => Boolean(active))
        .map(([stratKey]) => stratKey);

      await TradingService.toggleAutoTrade(
        clientTag,
        nextState,
        activeStrats.length > 0 ? activeStrats : null,
        multiplier || 1.0
      );
      masterOptIn = nextState;
      if (user) user.master_opt_in = nextState;
      f7.toast
        .create({
          text: `Auto-Trade ${nextState ? "🟢 ENABLED" : "⚪ DISABLED"} for ${clientTag}`,
          closeTimeout: 2000,
        })
        .open();
      dispatch("update", { clientTag, master_opt_in: nextState });
    } catch (e) {
      f7.dialog.alert(e.message, "Auto-Trade Toggle Failed");
    }
  }

  // Emergency Square-Off this Client
  function triggerClientEmergencySquareOff() {
    f7.dialog.confirm(
      `Are you sure you want to trigger an <b>Emergency Square-Off</b> for UCC <b>${clientTag}</b>?<br/><span style="color:#ef4444; font-size:12px;">All ${openPositions.length} open position(s) will be market squared off immediately.</span>`,
      `Emergency Square Off [${clientTag}]`,
      async () => {
        try {
          await TradingService.squareOffPosition({ ucc: clientTag, isSim: false });
          SoundAlertManager.playChime("KILLSWITCH");
          f7.toast
            .create({
              text: `⚡ Emergency Square-Off completed for ${clientTag}`,
              closeTimeout: 3000,
            })
            .open();
          dispatch("squareoff", { clientTag });
          dispatch("refresh");
        } catch (e) {
          f7.dialog.alert(e.message, "Square-Off Failed");
        }
      }
    );
  }

  // Quick In-Drawer Position Square Off
  function squareOffSinglePosition(pos) {
    dispatch("squareOffPosition", pos);
  }

  // Quick In-Drawer Order Cancel
  function cancelSingleOrder(ord) {
    dispatch("cancelOrder", ord);
  }

  // Re-assign UCC tag to selected user
  async function submitReassign() {
    if (!reassignSelectedUser) {
      f7.dialog.alert("Please select a user account to link with.", "User Required");
      return;
    }
    isReassigning = true;
    try {
      const payload = {
        user_id: reassignSelectedUser.user_id || reassignSelectedUser._id,
        mobile: reassignSelectedUser.mobile,
        client_tag: clientTag.toUpperCase(),
        ucc: clientTag.toUpperCase(),
        multiplier: multiplier,
        master_opt_in: masterOptIn,
      };

      await TradingService.assignUccTag(payload, false);

      f7.toast
        .create({
          text: `✓ UCC Tag ${clientTag} reassigned to ${reassignSelectedUser.fullName}!`,
          closeTimeout: 2500,
        })
        .open();
      dispatch("reassign", { clientTag, user: reassignSelectedUser });
      dispatch("refresh");
    } catch (err) {
      f7.dialog.alert(err.message, "Re-assignment Failed");
    } finally {
      isReassigning = false;
    }
  }

  onMount(() => {
    window.addEventListener("keydown", handleKeyDown);
  });

  onDestroy(() => {
    if (typeof window !== "undefined") {
      window.removeEventListener("keydown", handleKeyDown);
    }
    onDrawerClose();
  });
</script>

{#if opened}
  <!-- SEMI-TRANSPARENT BACKDROP (z-index: 10490) -->
  <div
    class="drawer-backdrop"
    on:click={closeDrawer}
    on:keydown={(e) => e.key === "Enter" && closeDrawer()}
    tabindex="-1"
    role="button"
    aria-label="Close Inspector Drawer Backdrop"
  />

  <!-- SLIDE-OVER DRAWER (z-index: 10500) -->
  <div
    class="client-inspector-drawer"
    bind:this={drawerEl}
    role="dialog"
    aria-modal="true"
    aria-labelledby="drawer-client-title"
  >
    <!-- TOP HEADER -->
    <div class="drawer-header">
      <div class="header-identity">
        <div class="identity-top">
          <span class="ucc-pill">{clientTag || "NO_UCC"}</span>
          <span
            class={`status-chip ${user?.account_status === "approved" ? "status-approved" : "status-pending"}`}
          >
            {user?.account_status || "ACTIVE"}
          </span>
        </div>
        <h2 id="drawer-client-title" class="client-name">
          {user?.fullName || "Unlinked / Anonymous Client"}
        </h2>
        <div class="client-submeta">
          <span>📞 {user?.mobile || "No Mobile"}</span>
          <span>•</span>
          <span>🪪 PAN: {user?.pan || "N/A"}</span>
          {#if user?.user_id}
            <span>•</span>
            <span class="user-id-mono">UID: {user.user_id.slice(-6)}</span>
          {/if}
        </div>
      </div>

      <div class="header-controls">
        <button
          type="button"
          class={`auto-trade-toggle-btn ${masterOptIn ? "btn-optin-on" : "btn-optin-off"}`}
          on:click={toggleMasterOptIn}
          title="Toggle algorithmic master execution for this UCC"
        >
          {masterOptIn ? "🟢 Auto-Trade ON" : "⚪ Auto-Trade OFF"}
        </button>
        <button
          type="button"
          class="close-drawer-btn"
          on:click={closeDrawer}
          aria-label="Close Drawer"
        >
          ✕
        </button>
      </div>
    </div>

    <!-- LIVE METRICS BANNER STRIP -->
    <div class="drawer-metrics-strip">
      <div class="metric-block">
        <span class="m-label">TOTAL P&amp;L</span>
        <span
          class={`m-val tabular-mono ${totalClientPnl >= 0 ? "val-green" : "val-red"}`}
        >
          {formatMoney(totalClientPnl)}
        </span>
      </div>
      <div class="metric-divider" />
      <div class="metric-block">
        <span class="m-label">CLIENT TIER</span>
        <span class="m-val">
          {#if isTierUnassigned(tier)}
            <span class="badge color-red font-bold" style="font-size: 11px;">⚠️ UNASSIGNED</span>
          {:else if tier === 'H'}
            <span class="badge color-red font-bold" style="font-size: 11px;">🔴 Tier H</span>
          {:else if tier === 'M'}
            <span class="badge color-yellow font-bold" style="font-size: 11px; color:#b45309;">🟡 Tier M</span>
          {:else if tier === 'L'}
            <span class="badge color-green font-bold" style="font-size: 11px;">🟢 Tier L</span>
          {:else if tier === 'CUSTOM'}
            <span class="badge color-blue font-bold" style="font-size: 11px;">⚪ Custom ({customLot}sh)</span>
          {/if}
        </span>
      </div>
      <div class="metric-divider" />
      <div class="metric-block">
        <span class="m-label">OPEN POSITIONS</span>
        <span class="m-val tabular-mono val-blue">{openPositions.length}</span>
      </div>
      <div class="metric-divider" />
      <div class="metric-block">
        <span class="m-label">ACTIVE ORDERS</span>
        <span class="m-val tabular-mono val-slate">{activeOrders.length}</span>
      </div>
      <div class="metric-divider" />
      <div class="metric-block">
        <span class="m-label">MULTIPLIER</span>
        <span class="m-val tabular-mono val-purple">{multiplier.toFixed(1)}x</span>
      </div>
    </div>

    <!-- TAB NAVIGATION BAR -->
    <div class="drawer-tabs">
      <button
        type="button"
        class={`tab-btn ${activeTab === "sizing" ? "active" : ""}`}
        on:click={() => (activeTab = "sizing")}
      >
        ⚡ F1F2 Tier &amp; Sizing
      </button>
      <button
        type="button"
        class={`tab-btn ${activeTab === "strategies" ? "active" : ""}`}
        on:click={() => (activeTab = "strategies")}
      >
        🧩 Strategies (7)
      </button>
      <button
        type="button"
        class={`tab-btn ${activeTab === "positions_orders" ? "active" : ""}`}
        on:click={() => (activeTab = "positions_orders")}
      >
        📊 Active ({openPositions.length + activeOrders.length})
      </button>
      <button
        type="button"
        class={`tab-btn ${activeTab === "reassign_audit" ? "active" : ""}`}
        on:click={() => (activeTab = "reassign_audit")}
      >
        🔗 Link &amp; Audit
      </button>
    </div>

    <!-- BODY CONTENT AREA (SCROLLABLE) -->
    <div class="drawer-body">
      <!-- TAB 1: SIZING & MULTIPLIER (F1F2 PARITY) -->
      {#if activeTab === "sizing"}
        <div class="tab-pane">
          <!-- 1. TIER SELECTOR -->
          <div class="section-card">
            <div class="section-title-wrap">
              <h3 class="section-title">🏷️ F1F2 Client Tier (Compulsory)</h3>
              <span class="section-hint">Determines base lot size per scrip</span>
            </div>

            {#if isTierUnassigned(tier)}
              <div class="tier-warning-banner">
                ⚠️ <strong>TIER UNASSIGNED:</strong> Client cannot execute orders until a Tier is assigned by Admin.
              </div>
            {/if}

            <div class="tier-segmented-grid">
              <button
                type="button"
                class={`tier-btn btn-tier-h ${tier === 'H' ? 'active' : ''}`}
                on:click={() => (tier = 'H')}
              >
                🔴 Tier H (High)
              </button>
              <button
                type="button"
                class={`tier-btn btn-tier-m ${tier === 'M' ? 'active' : ''}`}
                on:click={() => (tier = 'M')}
              >
                🟡 Tier M (Medium)
              </button>
              <button
                type="button"
                class={`tier-btn btn-tier-l ${tier === 'L' ? 'active' : ''}`}
                on:click={() => (tier = 'L')}
              >
                🟢 Tier L (Low)
              </button>
              <button
                type="button"
                class={`tier-btn btn-tier-custom ${tier === 'CUSTOM' ? 'active' : ''}`}
                on:click={() => (tier = 'CUSTOM')}
              >
                ⚪ Custom Lot
              </button>
            </div>

            {#if tier === 'CUSTOM'}
              <div class="custom-lot-input-row">
                <label for="custom-lot-input" class="form-label">Custom Base Lot (Shares):</label>
                <input
                  id="custom-lot-input"
                  type="number"
                  min="1"
                  step="1"
                  class="form-input tabular-mono"
                  placeholder="e.g. 75"
                  bind:value={customLot}
                />
                {#if !customLot || Number(customLot) <= 0}
                  <span class="field-error-text">⚠️ Custom lot size must be at least 1 share</span>
                {/if}
              </div>
            {/if}
          </div>

          <!-- 2. MULTIPLIER & CAPITAL SIZING -->
          <div class="section-card">
            <div class="section-title-wrap">
              <h3 class="section-title">⚡ Multiplier &amp; Capital Sizing</h3>
              <span class="section-hint">Scales base lots and down-averaging tranches</span>
            </div>

            <div class="multiplier-chips-grid">
              {#each [0.5, 1.0, 1.5, 2.0, 3.0, 5.0] as preset}
                <button
                  type="button"
                  class={`mult-chip ${multiplier === preset ? "chip-active" : ""}`}
                  on:click={() => applyMultiplier(preset)}
                >
                  {preset.toFixed(1)}x
                </button>
              {/each}
            </div>

            <div class="form-grid">
              <div class="form-group">
                <label for="mult-input" class="form-label">Multiplier Factor (0.1x – 10.0x)</label>
                <div class="mult-input-group">
                  <button
                    type="button"
                    class="stepper-btn"
                    on:click={() => applyMultiplier(Math.max(0.1, Math.round((multiplier - 0.5) * 10) / 10))}
                  >
                    -
                  </button>
                  <input
                    id="mult-input"
                    type="number"
                    step="0.1"
                    min="0.1"
                    max="10.0"
                    class="stepper-input tabular-mono"
                    bind:value={multiplier}
                    on:change={(e) => applyMultiplier(e.target.value)}
                  />
                  <button
                    type="button"
                    class="stepper-btn"
                    on:click={() => applyMultiplier(Math.min(10.0, Math.round((multiplier + 0.5) * 10) / 10))}
                  >
                    +
                  </button>
                </div>
              </div>

              <div class="form-group">
                <label for="dwnavg-select" class="form-label">Down-Avg Factor (dwnAvg_qty)</label>
                <select id="dwnavg-select" class="form-select" bind:value={dwnAvgQty}>
                  <option value={1}>1x (Same as Base Tranche)</option>
                  <option value={2}>2x (Double Lot on Dips)</option>
                </select>
                <span class="field-subtext">{dwnAvgQty}x base shares per dip</span>
              </div>

              <div class="form-group full-width">
                <label for="drawer-cap-input" class="form-label">Designated Capital Amount (₹)</label>
                <input
                  id="drawer-cap-input"
                  type="number"
                  step="10000"
                  class="form-input tabular-mono"
                  bind:value={amount}
                />
                <span class="field-subtext">{formatCapital(amount)} designated capital</span>
              </div>
            </div>
          </div>

          <!-- 3. LIVE STOCK QUANTITY PREVIEW -->
          <div class="section-card">
            <div class="section-title-wrap">
              <h3 class="section-title">📊 Live Stock Quantity Preview</h3>
              <span class="section-hint">Calculated shares based on Selected Tier ({tier || 'None'}) × {multiplier.toFixed(1)}x</span>
            </div>

            {#if isLoadingPreviewScrips}
              <div style="padding: 12px; text-align: center; color: #94a3b8; font-size: 12px;">Loading scrip matrix...</div>
            {:else}
              <div class="preview-table-container">
                <table class="preview-table">
                  <thead>
                    <tr>
                      <th>Scrip</th>
                      <th>BSE Code</th>
                      <th>Tier Lots (H / M / L)</th>
                      <th>Order Qty</th>
                      <th>Disclosed (10%)</th>
                      <th>Down-Avg Tranche</th>
                    </tr>
                  </thead>
                  <tbody>
                    {#each previewScrips as scrip}
                      {@const baseQty = tier === 'H' ? scrip.H_qty : tier === 'M' ? scrip.M_qty : tier === 'L' ? scrip.L_qty : tier === 'CUSTOM' ? (customLot || 0) : 0}
                      {@const calcQty = Math.round(baseQty * (multiplier || 1.0))}
                      {@const dqty = Math.max(1, Math.floor(calcQty / 10))}
                      {@const dwnQty = calcQty * (dwnAvgQty || 1)}
                      <tr>
                        <td class="font-bold text-white">{scrip.Stock_Name}</td>
                        <td class="tabular-mono text-slate">{scrip.Security_Code}</td>
                        <td class="tabular-mono text-slate">{scrip.H_qty} / {scrip.M_qty} / {scrip.L_qty}</td>
                        <td class="tabular-mono {calcQty > 0 ? 'text-green font-bold' : 'text-red'}">
                          {calcQty > 0 ? `${calcQty} sh` : (tier ? '0 sh' : '⚠️ Unassigned')}
                        </td>
                        <td class="tabular-mono text-cyan">{calcQty > 0 ? `${dqty} sh` : '-'}</td>
                        <td class="tabular-mono text-purple">{calcQty > 0 ? `${dwnQty} sh` : '-'}</td>
                      </tr>
                    {/each}
                  </tbody>
                </table>
              </div>
            {/if}

            <div class="card-action-bar" style="margin-top: 14px;">
              <button
                type="button"
                class="btn-save-primary"
                on:click={saveSizingConfig}
                disabled={isSavingConfig || (tier === 'CUSTOM' && (!customLot || Number(customLot) <= 0))}
              >
                {isSavingConfig ? "Saving..." : "💾 Save F1F2 Tier Configuration"}
              </button>
            </div>
          </div>
        </div>
      {/if}

      <!-- TAB 2: STRATEGY MATRIX (7 AUTHENTIC STRATEGIES) -->
      {#if activeTab === "strategies"}
        <div class="tab-pane">
          <div class="section-card">
            <div class="section-title-wrap">
              <h3 class="section-title">🧩 Authentic F1F2 Strategy Opt-Ins</h3>
              <span class="section-hint">Toggle live execution rules for this UCC</span>
            </div>

            <div class="strategy-list">
              <!-- S1 -->
              <div class="strategy-item">
                <div class="strat-info">
                  <div class="strat-title-row">
                    <span class="strat-tag tag-s1">S1</span>
                    <strong class="strat-name">VWAP Breakout</strong>
                  </div>
                  <p class="strat-desc">
                    Core momentum engine tracking VWAP break &amp; volume expansion.
                  </p>
                </div>
                <button
                  type="button"
                  class={`strat-toggle-btn ${strategies.S1 ? "on" : "off"}`}
                  on:click={() => toggleStrategy("S1")}
                  disabled={isSavingStrategies}
                >
                  {strategies.S1 ? "ACTIVE" : "PAUSED"}
                </button>
              </div>

              <!-- S2 -->
              <div class="strategy-item">
                <div class="strat-info">
                  <div class="strat-title-row">
                    <span class="strat-tag tag-s2">S2</span>
                    <strong class="strat-name">HML Momentum</strong>
                  </div>
                  <p class="strat-desc">
                    High-Minus-Low momentum breakout with ATR trailing management.
                  </p>
                </div>
                <button
                  type="button"
                  class={`strat-toggle-btn ${strategies.S2 ? "on" : "off"}`}
                  on:click={() => toggleStrategy("S2")}
                  disabled={isSavingStrategies}
                >
                  {strategies.S2 ? "ACTIVE" : "PAUSED"}
                </button>
              </div>

              <!-- S3 -->
              <div class="strategy-item">
                <div class="strat-info">
                  <div class="strat-title-row">
                    <span class="strat-tag tag-s3">S3</span>
                    <strong class="strat-name">Pullback &amp; Mean Reversion</strong>
                  </div>
                  <p class="strat-desc">
                    Buys deep oversold dips returning toward intraday moving averages.
                  </p>
                </div>
                <button
                  type="button"
                  class={`strat-toggle-btn ${strategies.S3 ? "on" : "off"}`}
                  on:click={() => toggleStrategy("S3")}
                  disabled={isSavingStrategies}
                >
                  {strategies.S3 ? "ACTIVE" : "PAUSED"}
                </button>
              </div>

              <!-- S4 -->
              <div class="strategy-item">
                <div class="strat-info">
                  <div class="strat-title-row">
                    <span class="strat-tag tag-s4">S4</span>
                    <strong class="strat-name">Gap Breakout</strong>
                  </div>
                  <p class="strat-desc">
                    Opening gap continuation and morning range expansion.
                  </p>
                </div>
                <button
                  type="button"
                  class={`strat-toggle-btn ${strategies.S4 ? "on" : "off"}`}
                  on:click={() => toggleStrategy("S4")}
                  disabled={isSavingStrategies}
                >
                  {strategies.S4 ? "ACTIVE" : "PAUSED"}
                </button>
              </div>

              <!-- DOWN_AVGING -->
              <div class="strategy-item">
                <div class="strat-info">
                  <div class="strat-title-row">
                    <span class="strat-tag tag-dwn">DOWN_AVG</span>
                    <strong class="strat-name">Staged Matrix Down-Averaging</strong>
                  </div>
                  <p class="strat-desc">
                    Ladders buy tranches at 0.20% dip increments up to matrix limit.
                  </p>
                </div>
                <button
                  type="button"
                  class={`strat-toggle-btn ${strategies.DOWN_AVGING ? "on" : "off"}`}
                  on:click={() => toggleStrategy("DOWN_AVGING")}
                  disabled={isSavingStrategies}
                >
                  {strategies.DOWN_AVGING ? "ACTIVE" : "PAUSED"}
                </button>
              </div>

              <!-- REV -->
              <div class="strategy-item">
                <div class="strat-info">
                  <div class="strat-title-row">
                    <span class="strat-tag tag-rev">REV</span>
                    <strong class="strat-name">Reverse Intraday Strategy</strong>
                  </div>
                  <p class="strat-desc">
                    Counter-trend mean reversion for high-RSI rejection reversals.
                  </p>
                </div>
                <button
                  type="button"
                  class={`strat-toggle-btn ${strategies.REV ? "on" : "off"}`}
                  on:click={() => toggleStrategy("REV")}
                  disabled={isSavingStrategies}
                >
                  {strategies.REV ? "ACTIVE" : "PAUSED"}
                </button>
              </div>

              <!-- CNC -->
              <div class="strategy-item">
                <div class="strat-info">
                  <div class="strat-title-row">
                    <span class="strat-tag tag-cnc">CNC</span>
                    <strong class="strat-name">Positional / Delivery Trades</strong>
                  </div>
                  <p class="strat-desc">
                    Cash delivery trades held positional across settlement cycles.
                  </p>
                </div>
                <button
                  type="button"
                  class={`strat-toggle-btn ${strategies.CNC ? "on" : "off"}`}
                  on:click={() => toggleStrategy("CNC")}
                  disabled={isSavingStrategies}
                >
                  {strategies.CNC ? "ACTIVE" : "PAUSED"}
                </button>
              </div>
            </div>
          </div>
        </div>
      {/if}

      <!-- TAB 3: ACTIVE POSITIONS & ORDERS -->
      {#if activeTab === "positions_orders"}
        <div class="tab-pane">
          <!-- Positions Section -->
          <div class="section-card">
            <div class="section-title-wrap">
              <h3 class="section-title">📊 Open Positions ({openPositions.length})</h3>
            </div>
            {#if openPositions.length === 0}
              <div class="empty-state-card">
                <span>No open positions for this client.</span>
              </div>
            {:else}
              <div class="drawer-table-wrap">
                <table class="drawer-table">
                  <thead>
                    <tr>
                      <th>Symbol</th>
                      <th>Qty</th>
                      <th>Avg / LTP</th>
                      <th>P&amp;L</th>
                      <th style="text-align:right;">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {#each openPositions as pos}
                      <tr>
                        <td>
                          <button
                            type="button"
                            class="symbol-chart-btn"
                            on:click={() => dispatch("openChart", { symbol: pos.symbol, ucc: clientTag })}
                            title="View live signal chart"
                            style="background:none; border:none; cursor:pointer; color:#3b82f6; font-weight:700; padding:0; display:inline-flex; align-items:center; gap:4px;"
                          >
                            <strong>{pos.symbol}</strong> <span style="font-size:11px;">📈</span>
                          </button>
                        </td>
                        <td
                          class={`tabular-mono ${pos.netQty > 0 ? "text-green" : pos.netQty < 0 ? "text-red" : ""}`}
                        >
                          {pos.netQty > 0 ? `+${pos.netQty}` : pos.netQty}
                        </td>
                        <td class="tabular-mono text-slate">
                          ₹{Number(pos.buyAvg || 0).toFixed(2)} / ₹{Number(pos.ltp || 0).toFixed(2)}
                        </td>
                        <td
                          class={`tabular-mono font-bold ${pos.pnl >= 0 ? "text-green" : "text-red"}`}
                        >
                          {formatMoney(pos.pnl)}
                        </td>
                        <td style="text-align:right;">
                          <button
                            type="button"
                            class="row-sqoff-btn"
                            on:click={() => squareOffSinglePosition(pos)}
                            title="Square off position"
                          >
                            ⚡ Sq-Off
                          </button>
                        </td>
                      </tr>
                    {/each}
                  </tbody>
                </table>
              </div>
            {/if}
          </div>

          <!-- Active Orders Section -->
          <div class="section-card">
            <div class="section-title-wrap">
              <h3 class="section-title">📋 Active Orders ({activeOrders.length})</h3>
            </div>
            {#if activeOrders.length === 0}
              <div class="empty-state-card">
                <span>No active/pending orders for this client.</span>
              </div>
            {:else}
              <div class="drawer-table-wrap">
                <table class="drawer-table">
                  <thead>
                    <tr>
                      <th>Order ID / Time</th>
                      <th>Side / Symbol</th>
                      <th>Qty / Price</th>
                      <th>Status</th>
                      <th style="text-align:right;">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {#each activeOrders as ord (ord.id)}
                      <tr class={pendingActionIds.has(ord.id) ? "row-pending-lease" : ""}>
                        <td class="tabular-mono text-slate">
                          <div>{ord.id.slice(-8)}</div>
                          <small style="opacity:0.7;">{ord.time}</small>
                        </td>
                        <td>
                          <span class={`side-badge ${ord.side === "BUY" ? "side-buy" : "side-sell"}`}>
                            {ord.side}
                          </span>
                          <strong>{ord.symbol}</strong>
                        </td>
                        <td class="tabular-mono">
                          {ord.qty} @ ₹{Number(ord.price || 0).toFixed(2)}
                        </td>
                        <td>
                          <span
                            class={`status-pill ${ord.status === "CANCELLING..." ? "status-cancelling" : "status-open"}`}
                          >
                            {ord.status}
                          </span>
                        </td>
                        <td style="text-align:right;">
                          <button
                            type="button"
                            class="row-cancel-btn"
                            on:click={() => cancelSingleOrder(ord)}
                            disabled={pendingActionIds.has(ord.id) || ord.status === "CANCELLING..."}
                          >
                            {pendingActionIds.has(ord.id) ? "⏳..." : "Cancel"}
                          </button>
                        </td>
                      </tr>
                    {/each}
                  </tbody>
                </table>
              </div>
            {/if}
          </div>
        </div>
      {/if}

      <!-- TAB 4: REASSIGN & AUDIT -->
      {#if activeTab === "reassign_audit"}
        <div class="tab-pane">
          <div class="section-card">
            <div class="section-title-wrap">
              <h3 class="section-title">🔗 Database UCC Tag Assignment</h3>
              <span class="section-hint">Link or transfer UCC {clientTag} to another user profile</span>
            </div>

            <div class="reassign-wrap">
              <label for="reassign-typeahead" class="form-label">Search Target Account</label>
              <UserSearchTypeahead
                id="reassign-typeahead"
                {users}
                placeholder="Search by full name, mobile or PAN..."
                bind:value={reassignSearchQuery}
                on:select={(e) => {
                  reassignSelectedUser = e.detail;
                  reassignSearchQuery = e.detail ? `${e.detail.fullName || ''} (${e.detail.mobile || ''})`.trim() : '';
                }}
              />

              {#if reassignSelectedUser}
                <div class="selected-target-user-card">
                  <div class="target-name">
                    👤 {reassignSelectedUser.fullName}
                  </div>
                  <div class="target-meta">
                    <span>📞 {reassignSelectedUser.mobile}</span>
                    <span>•</span>
                    <span>PAN: {reassignSelectedUser.pan || "N/A"}</span>
                  </div>
                  <button
                    type="button"
                    class="btn-save-reassign"
                    on:click={submitReassign}
                    disabled={isReassigning}
                  >
                    {isReassigning ? "Saving Link in DB..." : "💾 Confirm Link Transfer in DB"}
                  </button>
                </div>
              {/if}
            </div>
          </div>

          <div class="section-card">
            <AuditClientTimeline ucc={clientTag} {environment} />
          </div>
        </div>
      {/if}
    </div>

    <!-- FOOTER EMERGENCY STRIP -->
    <div class="drawer-footer">
      <div class="footer-actions-row">
        <button
          type="button"
          class="btn-emergency-sqoff"
          on:click={triggerClientEmergencySquareOff}
        >
          🚨 Square Off All Positions ({openPositions.length})
        </button>

        <a
          href={`/user-trading-controls/?ucc=${encodeURIComponent(clientTag)}&env=${environment}`}
          class="btn-full-controls"
          on:click={closeDrawer}
        >
          Full Controls &rarr;
        </a>
      </div>
    </div>
  </div>
{/if}

<style>
  /* --- LAYER & DRAWER POSITIONING --- */
  .drawer-backdrop {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.65);
    backdrop-filter: blur(2px);
    z-index: 10490;
    cursor: pointer;
  }

  .client-inspector-drawer {
    position: fixed;
    top: 0;
    right: 0;
    bottom: 0;
    width: min(580px, 100vw);
    background: #090d16;
    color: #f8fafc;
    z-index: 10500;
    display: flex;
    flex-direction: column;
    box-shadow: -8px 0 24px rgba(0, 0, 0, 0.5);
    border-left: 1px solid #334155;
    animation: slideInRight 0.25s cubic-bezier(0.16, 1, 0.3, 1) forwards;
  }

  @keyframes slideInRight {
    from {
      transform: translateX(100%);
    }
    to {
      transform: translateX(0);
    }
  }

  /* --- HEADER --- */
  .drawer-header {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    padding: 16px 20px;
    background: #0f172a;
    border-bottom: 1px solid #1e293b;
    gap: 16px;
  }

  .header-identity {
    flex: 1;
    min-width: 0;
  }

  .identity-top {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 4px;
    flex-wrap: wrap;
  }

  .ucc-pill {
    background: #3b82f6;
    color: #ffffff;
    font-size: 11px;
    font-weight: 800;
    padding: 2px 8px;
    border-radius: 4px;
    letter-spacing: 0.5px;
  }

  .status-chip {
    font-size: 10px;
    font-weight: 700;
    padding: 2px 6px;
    border-radius: 4px;
    text-transform: uppercase;
  }
  .status-approved {
    background: rgba(16, 185, 129, 0.15);
    color: #34d399;
    border: 1px solid #059669;
  }
  .status-pending {
    background: rgba(245, 158, 11, 0.15);
    color: #fbbf24;
    border: 1px solid #d97706;
  }

  .sim-badge {
    background: #581c87;
    color: #e9d5ff;
    border: 1px solid #a855f7;
    font-size: 10px;
    font-weight: 700;
    padding: 2px 6px;
    border-radius: 4px;
  }

  .client-name {
    margin: 0 0 4px 0;
    font-size: 16px;
    font-weight: 800;
    color: #f8fafc;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .client-submeta {
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 11px;
    color: #94a3b8;
    flex-wrap: wrap;
  }

  .user-id-mono {
    font-family: monospace;
    opacity: 0.8;
  }

  .header-controls {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .auto-trade-toggle-btn {
    border: none;
    font-size: 11px;
    font-weight: 700;
    padding: 4px 10px;
    border-radius: 14px;
    cursor: pointer;
    transition: all 0.2s ease;
  }
  .btn-optin-on {
    background: rgba(16, 185, 129, 0.2);
    color: #34d399;
    border: 1px solid #10b981;
  }
  .btn-optin-off {
    background: #1e293b;
    color: #94a3b8;
    border: 1px solid #334155;
  }

  .btn-drawer-order {
    background: #7c3aed;
    color: #ffffff;
    border: 1px solid #6d28d9;
    padding: 4px 10px;
    border-radius: 6px;
    font-size: 11px;
    font-weight: 700;
    cursor: pointer;
    transition: all 0.15s ease;
  }
  .btn-drawer-order:hover {
    background: #6d28d9;
  }

  .close-drawer-btn {
    background: transparent;
    border: 1px solid #334155;
    color: #94a3b8;
    width: 28px;
    height: 28px;
    border-radius: 6px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    font-size: 14px;
    transition: all 0.15s ease;
  }
  .close-drawer-btn:hover {
    background: #1e293b;
    color: #ffffff;
    border-color: #64748b;
  }

  /* --- METRICS STRIP --- */
  .drawer-metrics-strip {
    display: flex;
    align-items: center;
    justify-content: space-around;
    padding: 10px 16px;
    background: #0b1120;
    border-bottom: 1px solid #1e293b;
  }

  .metric-block {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 2px;
  }

  .m-label {
    font-size: 10px;
    font-weight: 700;
    color: #64748b;
    letter-spacing: 0.4px;
  }

  .m-val {
    font-size: 14px;
    font-weight: 800;
  }

  .metric-divider {
    width: 1px;
    height: 22px;
    background: #1e293b;
  }

  .val-green { color: #34d399; }
  .val-red { color: #f87171; }
  .val-blue { color: #60a5fa; }
  .val-slate { color: #cbd5e1; }
  .val-purple { color: #c084fc; }

  /* --- TABS --- */
  .drawer-tabs {
    display: flex;
    background: #0f172a;
    border-bottom: 1px solid #1e293b;
    padding: 4px 8px 0 8px;
    gap: 4px;
    overflow-x: auto;
  }

  .tab-btn {
    border: none;
    background: transparent;
    color: #94a3b8;
    font-size: 12px;
    font-weight: 700;
    padding: 8px 12px;
    border-radius: 6px 6px 0 0;
    cursor: pointer;
    white-space: nowrap;
    transition: all 0.15s ease;
  }
  .tab-btn:hover {
    color: #f8fafc;
  }
  .tab-btn.active {
    background: #1e293b;
    color: #38bdf8;
    border-bottom: 2px solid #38bdf8;
  }

  /* --- BODY --- */
  .drawer-body {
    flex: 1;
    overflow-y: auto;
    padding: 16px;
    display: flex;
    flex-direction: column;
    gap: 16px;
  }

  .tab-pane {
    display: flex;
    flex-direction: column;
    gap: 16px;
  }

  .section-card {
    background: #0f172a;
    border: 1px solid #1e293b;
    border-radius: 8px;
    padding: 14px 16px;
  }

  .section-title-wrap {
    display: flex;
    justify-content: space-between;
    align-items: baseline;
    margin-bottom: 12px;
  }

  .section-title {
    margin: 0;
    font-size: 13px;
    font-weight: 800;
    color: #f8fafc;
  }

  .section-hint {
    font-size: 11px;
    color: #64748b;
  }

  /* --- MULTIPLIER & SIZING STYLES --- */
  .multiplier-chips-grid {
    display: grid;
    grid-template-columns: repeat(6, 1fr);
    gap: 6px;
    margin-bottom: 12px;
  }

  .mult-chip {
    border: 1px solid #334155;
    background: #1e293b;
    color: #e2e8f0;
    font-size: 12px;
    font-weight: 700;
    padding: 6px 0;
    border-radius: 6px;
    cursor: pointer;
    transition: all 0.15s ease;
  }
  .mult-chip:hover {
    background: #334155;
    border-color: #64748b;
  }
  .mult-chip.chip-active {
    background: #2563eb;
    color: #ffffff;
    border-color: #3b82f6;
    box-shadow: 0 0 10px rgba(59, 130, 246, 0.4);
  }

  .custom-mult-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    padding-top: 8px;
    border-top: 1px solid #1e293b;
  }

  .mult-input-group {
    display: flex;
    align-items: center;
    gap: 4px;
  }

  .stepper-btn {
    width: 28px;
    height: 28px;
    border: 1px solid #334155;
    background: #1e293b;
    color: #f8fafc;
    border-radius: 4px;
    font-size: 14px;
    font-weight: 700;
    cursor: pointer;
  }
  .stepper-btn:hover {
    background: #334155;
  }

  .stepper-input {
    width: 60px;
    height: 28px;
    border: 1px solid #334155;
    background: #090d16;
    color: #f8fafc;
    text-align: center;
    border-radius: 4px;
    font-size: 12px;
    font-weight: 700;
  }

  .form-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 12px;
  }

  .form-group {
    display: flex;
    flex-direction: column;
    gap: 4px;
  }
  .form-group.full-width {
    grid-column: span 2;
  }

  .form-label {
    font-size: 11px;
    font-weight: 700;
    color: #94a3b8;
  }

  .form-input,
  .form-select {
    background: #090d16;
    border: 1px solid #334155;
    border-radius: 6px;
    padding: 6px 10px;
    font-size: 12px;
    color: #f8fafc;
    outline: none;
  }
  .form-input:focus,
  .form-select:focus {
    border-color: #38bdf8;
  }

  .field-subtext {
    font-size: 10px;
    color: #64748b;
  }

  .card-action-bar {
    margin-top: 14px;
    display: flex;
    justify-content: flex-end;
  }

  .btn-save-primary {
    background: #059669;
    color: #ffffff;
    border: none;
    padding: 8px 16px;
    border-radius: 6px;
    font-size: 12px;
    font-weight: 700;
    cursor: pointer;
    transition: all 0.15s ease;
  }
  .btn-save-primary:hover:not(:disabled) {
    background: #10b981;
  }

  /* --- STRATEGY LIST --- */
  .strategy-list {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  .strategy-item {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 10px 12px;
    background: #090d16;
    border: 1px solid #1e293b;
    border-radius: 6px;
    gap: 12px;
  }

  .strat-info {
    flex: 1;
  }

  .strat-title-row {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 2px;
  }

  .strat-tag {
    font-size: 10px;
    font-weight: 800;
    padding: 1px 6px;
    border-radius: 4px;
  }
  /* --- F1F2 TIER CONTROLS & PREVIEW TABLE --- */
  .tier-segmented-grid {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 8px;
    margin-bottom: 12px;
  }

  .tier-btn {
    border: 1px solid #334155;
    background: #1e293b;
    color: #cbd5e1;
    font-size: 12px;
    font-weight: 700;
    padding: 8px 4px;
    border-radius: 6px;
    cursor: pointer;
    transition: all 0.15s ease;
    text-align: center;
  }
  .tier-btn:hover {
    border-color: #64748b;
    background: #334155;
  }
  .tier-btn.active.btn-tier-h {
    background: #fee2e2;
    color: #b91c1c;
    border-color: #ef4444;
    box-shadow: 0 0 8px rgba(239, 68, 68, 0.3);
  }
  .tier-btn.active.btn-tier-m {
    background: #fef3c7;
    color: #b45309;
    border-color: #f59e0b;
    box-shadow: 0 0 8px rgba(245, 158, 11, 0.3);
  }
  .tier-btn.active.btn-tier-l {
    background: #dcfce7;
    color: #15803d;
    border-color: #22c55e;
    box-shadow: 0 0 8px rgba(34, 197, 94, 0.3);
  }
  .tier-btn.active.btn-tier-custom {
    background: #e0e7ff;
    color: #4338ca;
    border-color: #6366f1;
    box-shadow: 0 0 8px rgba(99, 102, 241, 0.3);
  }

  .tier-warning-banner {
    background: rgba(239, 68, 68, 0.15);
    border: 1px solid #ef4444;
    color: #fca5a5;
    border-radius: 6px;
    padding: 8px 12px;
    font-size: 11px;
    margin-bottom: 12px;
    line-height: 1.4;
  }

  .custom-lot-input-row {
    margin-top: 10px;
    display: flex;
    flex-direction: column;
    gap: 4px;
    background: #1e293b;
    padding: 10px 12px;
    border-radius: 6px;
    border: 1px dashed #64748b;
  }

  .field-error-text {
    font-size: 11px;
    color: #f87171;
    font-weight: 600;
  }

  .preview-table-container {
    overflow-x: auto;
    border: 1px solid #1e293b;
    border-radius: 6px;
    background: #090d16;
  }

  .preview-table {
    width: 100%;
    border-collapse: collapse;
    font-size: 11px;
    text-align: left;
  }
  .preview-table th {
    background: #1e293b;
    color: #94a3b8;
    padding: 8px 10px;
    font-weight: 700;
    text-transform: uppercase;
    font-size: 10px;
    letter-spacing: 0.3px;
    border-bottom: 1px solid #334155;
    white-space: nowrap;
  }
  .preview-table td {
    padding: 8px 10px;
    border-bottom: 1px solid rgba(255, 255, 255, 0.05);
    white-space: nowrap;
  }

  .tag-s3 { background: #064e3b; color: #6ee7b7; }
  .tag-s4 { background: #78350f; color: #fde68a; }
  .tag-dwn { background: #4c1d95; color: #c4b5fd; }

  .strat-name {
    font-size: 12px;
    color: #f1f5f9;
  }

  .strat-desc {
    margin: 0;
    font-size: 11px;
    color: #64748b;
  }

  .strat-toggle-btn {
    border: none;
    font-size: 11px;
    font-weight: 800;
    padding: 5px 12px;
    border-radius: 12px;
    cursor: pointer;
    transition: all 0.15s ease;
  }
  .strat-toggle-btn.on {
    background: #dcfce7;
    color: #15803d;
  }
  .strat-toggle-btn.off {
    background: #1e293b;
    color: #94a3b8;
    border: 1px solid #334155;
  }

  /* --- MINI TABLES --- */
  .drawer-table-wrap {
    overflow-x: auto;
  }

  .drawer-table {
    width: 100%;
    border-collapse: collapse;
    font-size: 11px;
  }

  .drawer-table th {
    background: #090d16;
    color: #64748b;
    text-align: left;
    padding: 6px 8px;
    font-weight: 700;
    border-bottom: 1px solid #1e293b;
  }

  .drawer-table td {
    padding: 6px 8px;
    border-bottom: 1px solid #1e293b;
  }

  .side-badge {
    font-size: 9px;
    font-weight: 800;
    padding: 1px 4px;
    border-radius: 2px;
    margin-right: 4px;
  }
  .side-buy { background: #166534; color: #86efac; }
  .side-sell { background: #991b1b; color: #fca5a5; }

  .status-pill {
    font-size: 10px;
    font-weight: 700;
    padding: 1px 6px;
    border-radius: 4px;
  }
  .status-open { background: #1e3a8a; color: #93c5fd; }
  .status-cancelling { background: #7f1d1d; color: #fca5a5; animation: pulse 1s infinite; }

  .row-sqoff-btn {
    background: #78350f;
    color: #fde68a;
    border: 1px solid #b45309;
    border-radius: 4px;
    padding: 2px 6px;
    font-size: 10px;
    font-weight: 700;
    cursor: pointer;
  }
  .row-sqoff-btn:hover {
    background: #b45309;
  }

  .row-cancel-btn {
    background: #7f1d1d;
    color: #fca5a5;
    border: 1px solid #dc2626;
    border-radius: 4px;
    padding: 2px 6px;
    font-size: 10px;
    font-weight: 700;
    cursor: pointer;
  }
  .row-cancel-btn:hover:not(:disabled) {
    background: #dc2626;
    color: #ffffff;
  }

  .empty-state-card {
    padding: 16px;
    text-align: center;
    font-size: 12px;
    color: #64748b;
    background: #090d16;
    border-radius: 6px;
  }

  /* --- REASSIGN SECTION --- */
  .reassign-wrap {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  .selected-target-user-card {
    background: #090d16;
    border: 1px solid #334155;
    border-radius: 6px;
    padding: 10px 12px;
    display: flex;
    flex-direction: column;
    gap: 6px;
    margin-top: 6px;
  }

  .target-name {
    font-size: 13px;
    font-weight: 800;
    color: #f8fafc;
  }

  .target-meta {
    font-size: 11px;
    color: #94a3b8;
    display: flex;
    gap: 8px;
  }

  .btn-save-reassign {
    align-self: flex-start;
    margin-top: 4px;
    background: #2563eb;
    color: #ffffff;
    border: none;
    padding: 6px 12px;
    border-radius: 4px;
    font-size: 11px;
    font-weight: 700;
    cursor: pointer;
  }
  .btn-save-reassign:hover:not(:disabled) {
    background: #3b82f6;
  }

  /* --- FOOTER --- */
  .drawer-footer {
    padding: 12px 16px;
    background: #0f172a;
    border-top: 1px solid #1e293b;
    display: flex;
    flex-direction: column;
    gap: 10px;
  }

  .sim-topup-strip {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .topup-label {
    font-size: 11px;
    font-weight: 700;
    color: #c084fc;
  }

  .btn-topup {
    background: #3b0764;
    border: 1px solid #7e22ce;
    color: #e9d5ff;
    padding: 3px 8px;
    border-radius: 4px;
    font-size: 11px;
    font-weight: 700;
    cursor: pointer;
  }
  .btn-topup:hover:not(:disabled) {
    background: #581c87;
  }

  .footer-actions-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
  }

  .btn-emergency-sqoff {
    background: #7f1d1d;
    border: 1px solid #dc2626;
    color: #fee2e2;
    padding: 8px 14px;
    border-radius: 6px;
    font-size: 12px;
    font-weight: 800;
    cursor: pointer;
    transition: all 0.15s ease;
  }
  .btn-emergency-sqoff:hover {
    background: #dc2626;
    color: #ffffff;
  }

  .btn-full-controls {
    background: #1e293b;
    border: 1px solid #334155;
    color: #93c5fd;
    text-decoration: none;
    padding: 8px 14px;
    border-radius: 6px;
    font-size: 12px;
    font-weight: 700;
    display: inline-flex;
    align-items: center;
    transition: all 0.15s ease;
  }
  .btn-full-controls:hover {
    background: #2563eb;
    color: #ffffff;
    border-color: #3b82f6;
  }

  /* --- TABULAR UTILITIES --- */
  .tabular-mono {
    font-variant-numeric: tabular-nums;
    font-family: -apple-system, BlinkMacSystemFont, "SF Mono", "Segoe UI Mono", monospace;
  }
  .font-bold { font-weight: 800; }
  .text-green { color: #34d399; }
  .text-red { color: #f87171; }
  .text-slate { color: #94a3b8; }

  .row-pending-lease {
    background: #450a0a !important;
  }
</style>
